/**
 * ScorerLoginPage.jsx
 * Dedicated scorer login — distinct sports-app aesthetic from admin login.
 * Redirects to /score on success (not /admin).
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Lock, Eye, EyeOff, KeyRound } from 'lucide-react';
import { Input } from '@/components/ui/input';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

const SPORTS = [
  { icon: '🏓', label: 'Table Tennis' },
  { icon: '🏸', label: 'Badminton' },
  { icon: '♟️', label: 'Chess' },
  { icon: '🎯', label: 'Carrom' },
  { icon: '🥒', label: 'Pickleball' },
  { icon: '🎾', label: 'Squash' },
];

export default function ScorerLoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, clearError, isAuthenticated } = useAuthStore();

  const [phone, setPhone] = useState('+91');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate('/score', { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizePhone = (p) => {
    if (!p) return '';
    let d = p.replace(/[\s\-().]/g, '').trim();
    if (!d) return '';
    if (d.startsWith('+')) return d;
    if (/^91\d{10}$/.test(d)) return '+' + d;
    if (/^\d{10}$/.test(d)) return '+91' + d;
    return d;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    clearError();
    const normPhone = normalizePhone(phone);
    if (!normPhone || normPhone.replace(/\s/g, '').length < 10) {
      toast.error('Enter a valid phone number');
      triggerShake();
      return;
    }
    if (!password) {
      toast.error('Enter your password');
      triggerShake();
      return;
    }
    if (normPhone !== phone) setPhone(normPhone);
    const result = await login(normPhone, password);
    if (result.success) {
      if (result.mustChangePassword) {
        navigate('/change-password', { replace: true });
      } else {
        const name = (result.userName || '').split(' ')[0];
        toast.success(name ? `Let's go, ${name}!` : 'Ready to score!');
        navigate('/score', { replace: true });
      }
    } else {
      triggerShake();
      toast.error('Invalid credentials. Check your phone and password.');
    }
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  return (
    <div className="min-h-screen bg-[#06080d] flex flex-col items-center justify-center relative overflow-hidden px-4">

      {/* ── Ambient background circles ──────────────────────────── */}
      <div className="absolute top-[-120px] right-[-120px] w-[380px] h-[380px] rounded-full bg-[rgba(34,197,94,0.04)] blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-80px] left-[-80px] w-[280px] h-[280px] rounded-full bg-[rgba(245,197,24,0.04)] blur-3xl pointer-events-none" />

      {/* ── Floating sport icons ─────────────────────────────────── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {SPORTS.map((s, i) => (
          <span key={i} className="absolute text-2xl select-none opacity-[0.06]"
            style={{
              top: `${[12, 25, 68, 80, 45, 58][i]}%`,
              left: `${[8, 85, 5, 90, 72, 18][i]}%`,
              transform: `rotate(${[-15, 12, -8, 20, -5, 18][i]}deg)`,
            }}>
            {s.icon}
          </span>
        ))}
      </div>

      {/* ── Scoreboard ticker at top ─────────────────────────────── */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-[#0d1117] border-b border-[#1d2230] flex items-center overflow-hidden">
        <div className="flex items-center gap-8 animate-[scrollTicker_20s_linear_infinite] whitespace-nowrap pl-4">
          {[...SPORTS, ...SPORTS].map((s, i) => (
            <span key={i} className="text-xs text-[#4a5568] font-mono tracking-widest flex items-center gap-1.5">
              <span>{s.icon}</span>
              <span className="uppercase">{s.label}</span>
              <span className="text-[#252a37] mx-2">·</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── Main card ────────────────────────────────────────────── */}
      <div className="w-full max-w-[360px] mt-8">

        {/* Live dot + role label */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="w-2 h-2 rounded-full bg-[#22c55e] animate-pulse flex-shrink-0" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#22c55e]">Scorer Portal</span>
        </div>

        {/* Title */}
        <h1 className="text-center font-black text-[#f0f2f7] mb-1" style={{ fontSize: '2.25rem', lineHeight: 1 }}>
          Ready to Score?
        </h1>
        <p className="text-center text-[#4a5568] text-sm mb-8">
          Sign in to start scoring matches
        </p>

        {/* Form card */}
        <div className={`bg-[#0d1117] border border-[#1d2230] rounded-2xl p-6 shadow-[0_0_40px_rgba(0,0,0,0.5)] transition-transform ${shake ? 'animate-[shake_0.4s_ease]' : ''}`}>

          <form onSubmit={handleLogin} className="space-y-4">
            {/* Phone */}
            <div>
              <label className="text-xs font-semibold text-[#4a5568] uppercase tracking-wider mb-2 block">
                Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4a5568]" />
                <Input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  onBlur={e => { const n = normalizePhone(e.target.value); if (n !== e.target.value) setPhone(n); }}
                  placeholder="+91 98765 43210"
                  data-testid="scorer-phone-input"
                  className="pl-9 h-12 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#2d3548] focus:border-[#22c55e] focus-visible:ring-0 focus-visible:ring-offset-0 rounded-xl transition-colors"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="text-xs font-semibold text-[#4a5568] uppercase tracking-wider mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4a5568]" />
                <Input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  data-testid="scorer-password-input"
                  className="pl-9 pr-10 h-12 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#2d3548] focus:border-[#22c55e] focus-visible:ring-0 focus-visible:ring-offset-0 rounded-xl transition-colors"
                />
                <button type="button" onClick={() => setShowPwd(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4a5568] hover:text-[#8a93a3] transition-colors">
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button type="submit" disabled={isLoading}
              data-testid="scorer-login-submit"
              className="w-full h-13 rounded-xl font-black text-base transition-all disabled:opacity-60
                         bg-[#22c55e] hover:bg-[#16a34a] text-[#06080d]
                         flex items-center justify-center gap-2 group mt-2"
              style={{ height: '3.25rem' }}>
              {isLoading ? (
                <>
                  <span className="h-4 w-4 border-2 border-[#06080d] border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Start Scoring
                  <svg className="h-4 w-4 group-hover:translate-x-0.5 transition-transform"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Password hint */}
          <div className="mt-5 flex items-center gap-2 px-3 py-2.5 bg-[rgba(34,197,94,0.05)] border border-[rgba(34,197,94,0.12)] rounded-xl">
            <KeyRound className="h-3.5 w-3.5 text-[#22c55e] flex-shrink-0" />
            <p className="text-[11px] text-[#4a5568] leading-snug">
              Default password:{' '}
              <code className="font-mono text-[#22c55e] bg-[rgba(34,197,94,0.08)] px-1.5 py-0.5 rounded text-xs">
                CIRCLES
              </code>
            </p>
          </div>
        </div>

        {/* Admin link */}
        <p className="text-center mt-6 text-xs text-[#2d3548]">
          Admin?{' '}
          <button onClick={() => navigate('/login')}
            className="text-[#4a5568] hover:text-[#8a93a3] underline underline-offset-2 transition-colors">
            Sign in here
          </button>
        </p>
      </div>

      {/* ── Inline keyframe styles ───────────────────────────────── */}
      <style>{`
        @keyframes scrollTicker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%       { transform: translateX(-6px); }
          40%       { transform: translateX(6px); }
          60%       { transform: translateX(-4px); }
          80%       { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
}
